import {
  WEBSITE_VISIBILITY,
  type WebsiteVisibility,
} from '@jazel-auto/ise-contracts/dist/inventory.js'

export const MANUAL_VEHICLE_WEBSITE_VISIBILITY_REASON_OPTIONS = [
  { value: 'RECON', label: 'In recon' },
  { value: 'PRICING', label: 'Pricing issue' },
  { value: 'PHOTOS', label: 'Missing photos' },
  { value: 'TEMP_HOLD', label: 'Temporary hold' },
  { value: 'RESERVED', label: 'Reserved / deposit taken' },
  { value: 'SOLD_PENDING', label: 'Sold pending delivery / funding' },
  { value: 'OTHER', label: 'Other' },
] as const

export const MANUAL_VEHICLE_WEBSITE_VISIBILITY_PRESET_VALUES =
  MANUAL_VEHICLE_WEBSITE_VISIBILITY_REASON_OPTIONS.map((option) => option.value).filter(
    (value) => value !== 'OTHER',
  )

export type ManualWebsiteVisibilityBaseline = {
  websiteVisibility: WebsiteVisibility
  websiteVisibilityReason?: string
}

export function resolveManualWebsiteVisibilityReason(
  visibility: WebsiteVisibility,
  selectedReason: string,
  reasonNotes: string,
): string | undefined {
  if (visibility !== WEBSITE_VISIBILITY.HIDDEN) return undefined
  const reason = String(selectedReason || '').trim()
  if (reason === 'OTHER') return String(reasonNotes || '').trim() || undefined
  return reason || undefined
}

export function hydrateManualWebsiteVisibilityReason(
  storedReason: string | undefined | null,
): { selectedReason: string; reasonNotes: string } {
  const raw = String(storedReason || '').trim()
  if (!raw) {
    return { selectedReason: 'TEMP_HOLD', reasonNotes: '' }
  }
  if (
    MANUAL_VEHICLE_WEBSITE_VISIBILITY_PRESET_VALUES.includes(
      raw as (typeof MANUAL_VEHICLE_WEBSITE_VISIBILITY_PRESET_VALUES)[number],
    )
  ) {
    return { selectedReason: raw, reasonNotes: '' }
  }
  return { selectedReason: 'OTHER', reasonNotes: raw }
}

export function normalizeManualWebsiteVisibility(
  value: unknown,
): WebsiteVisibility {
  return value === WEBSITE_VISIBILITY.HIDDEN
    ? WEBSITE_VISIBILITY.HIDDEN
    : WEBSITE_VISIBILITY.VISIBLE
}

export function validateManualWebsiteVisibilityForm(params: {
  websiteVisibility: WebsiteVisibility
  selectedReason: string
  reasonNotes: string
}): Record<string, string> {
  const errors: Record<string, string> = {}
  if (params.websiteVisibility !== WEBSITE_VISIBILITY.HIDDEN) return errors

  const reason = String(params.selectedReason || '').trim()
  if (!reason) errors.websiteVisibilityReason = 'Required'
  if (reason === 'OTHER' && !String(params.reasonNotes || '').trim()) {
    errors.websiteVisibilityReasonNotes = 'Required'
  }
  return errors
}

export function buildManualWebsiteVisibilityBaseline(params: {
  websiteVisibility: WebsiteVisibility
  selectedReason: string
  reasonNotes: string
}): ManualWebsiteVisibilityBaseline {
  return {
    websiteVisibility: params.websiteVisibility,
    websiteVisibilityReason: resolveManualWebsiteVisibilityReason(
      params.websiteVisibility,
      params.selectedReason,
      params.reasonNotes,
    ),
  }
}

export function hasManualWebsiteVisibilityDiff(
  current: ManualWebsiteVisibilityBaseline,
  baseline: ManualWebsiteVisibilityBaseline,
): boolean {
  const currentReason =
    current.websiteVisibility === WEBSITE_VISIBILITY.HIDDEN
      ? current.websiteVisibilityReason
      : undefined
  const baselineReason =
    baseline.websiteVisibility === WEBSITE_VISIBILITY.HIDDEN
      ? baseline.websiteVisibilityReason
      : undefined

  return (
    current.websiteVisibility !== baseline.websiteVisibility ||
    currentReason !== baselineReason
  )
}

export function readManualWebsiteVisibilityFromSources(params: {
  operationalPatch?: Record<string, unknown> | null
  vehicle?: Record<string, unknown> | null
  respWebsiteVisibility?: unknown
  respWebsiteVisibilityReason?: unknown
}): ManualWebsiteVisibilityBaseline {
  const websiteVisibility = normalizeManualWebsiteVisibility(
    params.operationalPatch?.websiteVisibility ??
      params.respWebsiteVisibility ??
      params.vehicle?.websiteVisibility,
  )
  const storedReason =
    (params.operationalPatch?.websiteVisibilityReason as string | undefined) ??
    (params.vehicle?.websiteVisibilityReason as string | undefined) ??
    (params.respWebsiteVisibilityReason as string | undefined)
  const { selectedReason, reasonNotes } = hydrateManualWebsiteVisibilityReason(storedReason)

  return buildManualWebsiteVisibilityBaseline({
    websiteVisibility,
    selectedReason,
    reasonNotes,
  })
}
